import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { API_BASE_URL } from "./config";
import "./PublicProfile.css";

const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [relationshipStatus, setRelationshipStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Theme configurations
  const themes = {
    default: {
      primary: '#007bff',
      secondary: '#6c757d',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      text: '#333',
      card: '#fff'
    },
    dark: {
      primary: '#bb86fc',
      secondary: '#03dac6',
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      text: '#fff',
      card: '#1f1f1f'
    },
    ocean: {
      primary: '#00b4d8',
      secondary: '#0077b6',
      background: 'linear-gradient(135deg, #48cae4 0%, #0096c7 100%)',
      text: '#fff',
      card: '#f8f9fa'
    },
    sunset: {
      primary: '#ff6b6b',
      secondary: '#feca57',
      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      text: '#333',
      card: '#fff'
    },
    forest: {
      primary: '#52b788',
      secondary: '#40916c',
      background: 'linear-gradient(135deg, #2d6a4f 0%, #74c69d 100%)',
      text: '#fff',
      card: '#f8f9fa'
    },
    vintage: {
      primary: '#c9ada7',
      secondary: '#9a8c98',
      background: 'linear-gradient(135deg, #4a4e69 0%, #9a8c98 100%)',
      text: '#f2e9e4',
      card: '#22223b'
    }
  };

  const fetchUserProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/users/${username}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setUser(res.data.user);
      setRelationshipStatus(res.data.relationshipStatus);
      setEditData({
        fullName: res.data.user.fullName || '',
        tagline: res.data.user.tagline || '',
        bio: res.data.user.bio || '',
        city: res.data.user.city || '',
        age: res.data.user.age || '',
        profileTheme: res.data.user.profileTheme || 'default',
        accentColor: res.data.user.accentColor || '#007bff',
        socialLinks: res.data.user.socialLinks || {},
        interests: res.data.user.interests || []
      });

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || "Error loading profile");
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleFollow = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      await axios.post(
        `${API_BASE_URL}/api/users/${username}/follow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRelationshipStatus({
        ...relationshipStatus,
        hasRequestedFollow: true
      });
    } catch (err) {
      setError(err.response?.data?.error || "Error sending follow request");
    }
  };

  const handleCancelFollowRequest = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/users/${username}/cancel-follow-request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRelationshipStatus({
        ...relationshipStatus,
        hasRequestedFollow: false
      });
    } catch (err) {
      setError(err.response?.data?.error || "Error cancelling follow request");
    }
  };

  const handleUnfollow = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/users/${username}/unfollow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRelationshipStatus({
        ...relationshipStatus,
        isFollowing: false
      });
      setUser({
        ...user,
        followersCount: user.followersCount - 1
      });
    } catch (err) {
      setError(err.response?.data?.error || "Error unfollowing user");
    }
  };

  const handleBlock = async () => {
    if (!window.confirm("Are you sure you want to block this user? You will no longer see each other's content.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/users/${username}/block`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRelationshipStatus({
        ...relationshipStatus,
        hasBlocked: true,
        isFollowing: false,
        isFollower: false
      });
    } catch (err) {
      setError(err.response?.data?.error || "Error blocking user");
    }
  };

  const handleUnblock = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/users/${username}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRelationshipStatus({
        ...relationshipStatus,
        hasBlocked: false
      });
    } catch (err) {
      setError(err.response?.data?.error || "Error unblocking user");
    }
  };

  const handleMessage = () => {
    navigate(`/messages/${user._id}`);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setUploadingFiles(true);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      
      // Add text fields
      Object.keys(editData).forEach(key => {
        if (key === 'socialLinks' || key === 'interests') {
          formData.append(key, JSON.stringify(editData[key]));
        } else if (key !== 'profilePicFile' && key !== 'heroBackgroundFile') {
          formData.append(key, editData[key]);
        }
      });

      // Add file uploads
      if (editData.profilePicFile) {
        formData.append('profilePic', editData.profilePicFile);
      }
      if (editData.heroBackgroundFile) {
        formData.append('heroBackground', editData.heroBackgroundFile);
      }

      await axios.post(`${API_BASE_URL}/profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowEditModal(false);
      fetchUserProfile(); // Refresh profile data
    } catch (err) {
      setError(err.response?.data?.message || "Error updating profile");
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleAddInterest = () => {
    const interest = prompt("Enter a new interest:");
    if (interest && !editData.interests.includes(interest)) {
      setEditData({
        ...editData,
        interests: [...editData.interests, interest]
      });
    }
  };

  const handleRemoveInterest = (interest) => {
    setEditData({
      ...editData,
      interests: editData.interests.filter(i => i !== interest)
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <Link to="/" className="btn btn-primary">Go Home</Link>
      </div>
    );
  }

  const currentTheme = themes[user?.profileTheme || 'default'];
  const heroStyle = user?.heroBackground 
    ? { backgroundImage: `url(${API_BASE_URL}/${user.heroBackground})` }
    : { background: currentTheme.background };

  return (
    <div className="profile-page" style={{ '--accent-color': user?.accentColor || currentTheme.primary }}>
      {/* Hero Section */}
      <div className="hero-section" style={heroStyle}>
        <div className="hero-overlay">
          <div className="container">
            <div className="row align-items-end" style={{ minHeight: '400px' }}>
              <div className="col-12">
                <div className="profile-header-content">
                  <div className="d-flex align-items-end mb-4">
                    <div className="profile-pic-wrapper">
                      <img
                        src={user?.profilePic ? `${API_BASE_URL}/${user.profilePic}` : "https://via.placeholder.com/150"}
                        alt="Profile"
                        className="profile-pic-large"
                      />
                    </div>
                    <div className="ms-4 mb-3 text-white">
                      <h1 className="display-4 mb-0">{user?.fullName || user?.displayUsername}</h1>
                      <p className="h5 opacity-75">@{user?.displayUsername}</p>
                      {user?.tagline && <p className="lead mt-2">{user.tagline}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mt-4">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError("")}></button>
          </div>
        )}

        <div className="row">
          {/* Left Sidebar */}
          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">About</h5>
                {user?.bio && <p className="card-text">{user.bio}</p>}
                
                <div className="info-list mt-3">
                  {user?.city && (
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-geo-alt me-2"></i>
                      <span>{user.city}</span>
                    </div>
                  )}
                  {user?.age && (
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-calendar3 me-2"></i>
                      <span>{user.age} years old</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center mb-2">
                    <i className="bi bi-calendar-date me-2"></i>
                    <span>Joined {new Date(user?.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Social Links */}
                {user?.socialLinks && Object.keys(user.socialLinks).length > 0 && (
                  <div className="social-links mt-3">
                    <h6>Connect</h6>
                    <div className="d-flex gap-2">
                      {user.socialLinks.facebook && (
                        <a href={user.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-facebook"></i>
                        </a>
                      )}
                      {user.socialLinks.twitter && (
                        <a href={user.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                          <i className="bi bi-twitter"></i>
                        </a>
                      )}
                      {user.socialLinks.instagram && (
                        <a href={user.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                          <i className="bi bi-instagram"></i>
                        </a>
                      )}
                      {user.socialLinks.linkedin && (
                        <a href={user.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="bi bi-linkedin"></i>
                        </a>
                      )}
                      {user.socialLinks.website && (
                        <a href={user.socialLinks.website} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                          <i className="bi bi-globe"></i>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-4">
                  {relationshipStatus.isOwnProfile ? (
                    <button 
                      className="btn btn-primary w-100"
                      onClick={() => setShowEditModal(true)}
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Edit Profile
                    </button>
                  ) : (
                    <>
                      {!relationshipStatus.hasBlocked && (
                        <>
                          {relationshipStatus.isFollowing ? (
                            <button className="btn btn-outline-primary w-100 mb-2" onClick={handleUnfollow}>
                              <i className="bi bi-person-check-fill me-2"></i>
                              Following
                            </button>
                          ) : relationshipStatus.hasRequestedFollow ? (
                            <button className="btn btn-warning w-100 mb-2" onClick={handleCancelFollowRequest}>
                              <i className="bi bi-hourglass-split me-2"></i>
                              Request Sent
                            </button>
                          ) : (
                            <button className="btn btn-primary w-100 mb-2" onClick={handleFollow}>
                              <i className="bi bi-person-plus me-2"></i>
                              Follow
                            </button>
                          )}

                          {relationshipStatus.isFollowing && relationshipStatus.isFollowedBy && (
                            <button className="btn btn-success w-100 mb-2" onClick={handleMessage}>
                              <i className="bi bi-chat-dots me-2"></i>
                              Message
                            </button>
                          )}

                          <button className="btn btn-outline-danger w-100" onClick={handleBlock}>
                            <i className="bi bi-slash-circle me-2"></i>
                            Block
                          </button>
                        </>
                      )}

                      {relationshipStatus.hasBlocked && (
                        <button className="btn btn-warning w-100" onClick={handleUnblock}>
                          <i className="bi bi-slash-circle me-2"></i>
                          Unblock User
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Interests */}
            {user?.interests && user.interests.length > 0 && (
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title">Interests</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {user.interests.map((interest, index) => (
                      <span key={index} className="badge rounded-pill" style={{ backgroundColor: user.accentColor || currentTheme.primary }}>
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="col-md-8">
            {/* Stats */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-6">
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => setShowFollowers(!showFollowers)}
                    >
                      <h3 className="mb-0">{user?.followersCount || 0}</h3>
                      <p className="text-muted mb-0">Followers</p>
                    </button>
                  </div>
                  <div className="col-6">
                    <button 
                      className="btn btn-link text-decoration-none text-dark p-0"
                      onClick={() => setShowFollowing(!showFollowing)}
                    >
                      <h3 className="mb-0">{user?.followingCount || 0}</h3>
                      <p className="text-muted mb-0">Following</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Followers List */}
            {showFollowers && user?.followers && user.followers.length > 0 && (
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Followers</h5>
                  <button className="btn-close" onClick={() => setShowFollowers(false)}></button>
                </div>
                <div className="card-body">
                  <div className="list-group list-group-flush">
                    {user.followers.map(follower => (
                      <Link 
                        key={follower._id} 
                        to={`/${follower.displayUsername}`}
                        className="list-group-item list-group-item-action d-flex align-items-center"
                      >
                        <img
                          src={follower.profilePic ? `${API_BASE_URL}/${follower.profilePic}` : "https://via.placeholder.com/40"}
                          alt={follower.displayUsername}
                          className="rounded-circle me-3"
                          style={{ width: "40px", height: "40px", objectFit: "cover" }}
                        />
                        <div>
                          <strong>{follower.fullName || follower.displayUsername}</strong>
                          <br />
                          <small className="text-muted">@{follower.displayUsername}</small>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Following List */}
            {showFollowing && user?.following && user.following.length > 0 && (
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Following</h5>
                  <button className="btn-close" onClick={() => setShowFollowing(false)}></button>
                </div>
                <div className="card-body">
                  <div className="list-group list-group-flush">
                    {user.following.map(following => (
                      <Link 
                        key={following._id} 
                        to={`/${following.displayUsername}`}
                        className="list-group-item list-group-item-action d-flex align-items-center"
                      >
                        <img
                          src={following.profilePic ? `${API_BASE_URL}/${following.profilePic}` : "https://via.placeholder.com/40"}
                          alt={following.displayUsername}
                          className="rounded-circle me-3"
                          style={{ width: "40px", height: "40px", objectFit: "cover" }}
                        />
                        <div>
                          <strong>{following.fullName || following.displayUsername}</strong>
                          <br />
                          <small className="text-muted">@{following.displayUsername}</small>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && relationshipStatus.isOwnProfile && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Profile</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSaveProfile}>
                  {/* Basic Info */}
                  <h6 className="text-muted mb-3">Basic Information</h6>
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editData.fullName}
                      onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Tagline</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editData.tagline}
                      onChange={(e) => setEditData({ ...editData, tagline: e.target.value })}
                      placeholder="A short description about yourself"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Bio</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={editData.bio}
                      onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editData.city}
                        onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Age</label>
                      <input
                        type="number"
                        className="form-control"
                        value={editData.age}
                        onChange={(e) => setEditData({ ...editData, age: e.target.value })}
                        min="1"
                        max="120"
                      />
                    </div>
                  </div>

                  {/* Profile Customization */}
                  <h6 className="text-muted mb-3 mt-4">Profile Customization</h6>
                  
                  <div className="mb-3">
                    <label className="form-label">Profile Picture</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => setEditData({ ...editData, profilePicFile: e.target.files[0] })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Hero Background Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={(e) => setEditData({ ...editData, heroBackgroundFile: e.target.files[0] })}
                    />
                    <small className="form-text text-muted">Upload a custom background for your profile header</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Profile Theme</label>
                    <select
                      className="form-select"
                      value={editData.profileTheme}
                      onChange={(e) => setEditData({ ...editData, profileTheme: e.target.value })}
                    >
                      <option value="default">Default</option>
                      <option value="dark">Dark</option>
                      <option value="ocean">Ocean</option>
                      <option value="sunset">Sunset</option>
                      <option value="forest">Forest</option>
                      <option value="vintage">Vintage</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Accent Color</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="color"
                        className="form-control form-control-color me-2"
                        value={editData.accentColor}
                        onChange={(e) => setEditData({ ...editData, accentColor: e.target.value })}
                        style={{ width: '100px' }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={editData.accentColor}
                        onChange={(e) => setEditData({ ...editData, accentColor: e.target.value })}
                        placeholder="#007bff"
                      />
                    </div>
                  </div>

                  {/* Interests */}
                  <h6 className="text-muted mb-3 mt-4">Interests</h6>
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {editData.interests?.map((interest, index) => (
                        <span key={index} className="badge bg-primary">
                          {interest}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2"
                            style={{ fontSize: '0.7rem' }}
                            onClick={() => handleRemoveInterest(interest)}
                          ></button>
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleAddInterest}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add Interest
                    </button>
                  </div>

                  {/* Social Links */}
                  <h6 className="text-muted mb-3 mt-4">Social Links</h6>
                  <div className="mb-3">
                    <label className="form-label">Facebook</label>
                    <input
                      type="url"
                      className="form-control"
                      value={editData.socialLinks?.facebook || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        socialLinks: { ...editData.socialLinks, facebook: e.target.value }
                      })}
                      placeholder="https://facebook.com/yourprofile"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Twitter</label>
                    <input
                      type="url"
                      className="form-control"
                      value={editData.socialLinks?.twitter || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        socialLinks: { ...editData.socialLinks, twitter: e.target.value }
                      })}
                      placeholder="https://twitter.com/yourhandle"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Instagram</label>
                    <input
                      type="url"
                      className="form-control"
                      value={editData.socialLinks?.instagram || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        socialLinks: { ...editData.socialLinks, instagram: e.target.value }
                      })}
                      placeholder="https://instagram.com/yourprofile"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">LinkedIn</label>
                    <input
                      type="url"
                      className="form-control"
                      value={editData.socialLinks?.linkedin || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        socialLinks: { ...editData.socialLinks, linkedin: e.target.value }
                      })}
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Personal Website</label>
                    <input
                      type="url"
                      className="form-control"
                      value={editData.socialLinks?.website || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        socialLinks: { ...editData.socialLinks, website: e.target.value }
                      })}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleSaveProfile}
                  disabled={uploadingFiles}
                >
                  {uploadingFiles ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProfile;